import { Button, Chip, Slider, Checkbox } from "@heroui/react";
import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React, { useState, useRef } from "react";
import ToolPageLayout from "../../components/tools/ToolPageLayout";
import SignUpPromptModal from "../../components/tools/SignUpPromptModal";

const QuizGenerator: React.FC = () => {
  const [textInput, setTextInput] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [questionCount, setQuestionCount] = useState(10);
  const [questionTypes, setQuestionTypes] = useState({
    multipleChoice: true,
    trueFalse: true,
    shortAnswer: false,
    fillBlanks: false,
  });
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleGenerate = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setShowSignUpModal(true);
    }, 2000);
  };

  const canSubmit = () => {
    const hasContent = textInput.trim().length > 0 || selectedFile !== null;
    const hasQuestionType = Object.values(questionTypes).some(v => v);
    return hasContent && hasQuestionType;
  };

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "AI Quiz Generator - YouWo AI",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Generate practice quizzes automatically from any text, PDF, or study material. Free AI-powered quiz maker for students and educators.",
  };

  const howItWorksSteps = [
    {
      title: "Add Your Content",
      description: "Paste text, upload a PDF, or provide study material for quiz generation.",
    },
    {
      title: "AI Creates Questions",
      description: "Our AI analyzes content and generates relevant questions with answers.",
    },
    {
      title: "Take Your Quiz",
      description: "Test yourself with immediate feedback and detailed explanations.",
    },
  ];

  const benefits = [
    {
      title: "100+ Languages",
      description: "Generate quizzes from content in over 100 languages.",
      icon: "lucide:globe",
    },
    {
      title: "Multiple Question Types",
      description: "Generate MCQs, true/false, short answer, and fill-in-the-blank questions.",
      icon: "lucide:list-checks",
    },
    {
      title: "Answer Explanations",
      description: "Each question comes with detailed explanations to enhance learning.",
      icon: "lucide:message-circle-question",
    },
    {
      title: "Difficulty Settings",
      description: "Control the complexity of generated questions to match your level.",
      icon: "lucide:sliders",
    },
    {
      title: "Track Progress",
      description: "Review your quiz results and identify areas for improvement.",
      icon: "lucide:bar-chart-2",
    },
    {
      title: "Export with Pro",
      description: "Export quizzes to share with classmates or students with Pro.",
      icon: "lucide:share-2",
    },
  ];

  const faqs = [
    {
      question: "What is the AI Quiz Generator?",
      answer: "The AI Quiz Generator is a free tool that automatically creates practice quizzes from any text, PDF, or study material. It generates various question types with correct answers and explanations.",
    },
    {
      question: "What types of questions can it generate?",
      answer: "Our AI can generate multiple choice questions, true/false questions, short answer questions, and fill-in-the-blank questions. You can choose which types to include in your quiz.",
    },
    {
      question: "How accurate are the questions?",
      answer: "Our AI is trained to generate relevant, accurate questions based on your content. All questions are fact-checked against the source material you provide.",
    },
    {
      question: "Can teachers use this for creating tests?",
      answer: "Yes! Teachers and educators can use the Quiz Generator to create practice tests, homework assignments, and study materials for their students.",
    },
    {
      question: "Can I edit the generated questions?",
      answer: "Absolutely! All generated questions can be edited, removed, or supplemented with your own questions before sharing or taking the quiz.",
    },
    {
      question: "Is there a limit on questions?",
      answer: "Free users can generate up to 20 questions per quiz. Sign up for a free account to generate unlimited questions and save quizzes.",
    },
  ];

  const relatedTools = [
    {
      name: "AI Flashcard Maker",
      description: "Generate study flashcards automatically from any content.",
      href: "/tools/flashcard-maker",
      icon: "lucide:layers",
      iconColor: "text-purple-500",
    },
    {
      name: "PDF Summarizer",
      description: "Turn lengthy PDFs and documents into clear, concise summaries instantly.",
      href: "/tools/pdf-summarizer",
      icon: "lucide:file-text",
      iconColor: "text-orange-500",
    },
    {
      name: "PDF to Study Guide",
      description: "Turn PDFs into source-cited study guides, quizzes, and flashcards.",
      href: "/tools/pdf-to-study-guide",
      icon: "lucide:book-open-check",
      iconColor: "text-cornflowerblue",
    },
    {
      name: "Lecture Notes AI",
      description: "Transform lecture recordings into comprehensive study notes.",
      href: "/tools/lecture-notes",
      icon: "lucide:graduation-cap",
      iconColor: "text-emerald-500",
    },
    {
      name: "YouTube Summarizer",
      description: "Transform YouTube videos into concise summaries with timestamps.",
      href: "/tools/youtube-summarizer",
      icon: "lucide:youtube",
      iconColor: "text-red-500",
    },
  ];

  return (
    <>
      <ToolPageLayout
        title="Free AI Quiz Generator - Create Practice Tests Instantly | YouWo AI"
        metaDescription="Generate practice quizzes automatically from any text, PDF, or study material. Free AI-powered quiz maker with multiple choice, true/false, and short answer questions."
        keywords="quiz generator, ai quiz maker, practice test generator, mcq generator, study quiz, exam prep, quiz creator"
        canonicalUrl="https://youwo.ai/tools/quiz-generator"
        structuredData={structuredData}
        heroTitle="AI Quiz Generator"
        heroSubtitle="Generate practice quizzes from any text, PDF, or study material. Supports 100+ languages. Test your knowledge with AI-powered questions and instant feedback."
        heroIcon="lucide:help-circle"
        heroIconColor="text-green-500"
        heroIconBg="bg-green-50"
        howItWorksSteps={howItWorksSteps}
        benefitsTitle="Why Choose YouWo AI Quiz Generator?"
        benefits={benefits}
        faqs={faqs}
        relatedTools={relatedTools}
      >
        {/* Tool Input Area */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8">
          {/* Text Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Paste your study material
            </label>
            <textarea
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Paste lecture notes, textbook content, or any text to generate quiz questions..."
              className="w-full h-40 p-4 border border-gray-200 rounded-xl resize-none focus:outline-none focus:border-cornflowerblue"
            />
          </div>

          {/* Or Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-sm text-gray-400">or upload a file</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* File Upload */}
          <div
            className={`border-2 border-dashed rounded-xl p-4 text-center transition-colors cursor-pointer mb-6 ${
              isDragging
                ? "border-cornflowerblue bg-blue-50"
                : selectedFile
                ? "border-green-400 bg-green-50"
                : "border-gray-200 hover:border-cornflowerblue"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".pdf,.txt,.doc,.docx"
              onChange={handleFileSelect}
            />

            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <Icon icon="lucide:file-check" className="text-green-600 text-xl" />
                <span className="text-gray-900">{selectedFile.name}</span>
                <Button
                  variant="light"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                >
                  Remove
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <Icon icon="lucide:upload" className="text-xl" />
                <span>Upload PDF, DOC, or TXT file</span>
              </div>
            )}
          </div>

          {/* Settings */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Question Count */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Number of questions: {questionCount}
              </label>
              <Slider
                size="sm"
                step={5}
                minValue={5}
                maxValue={50}
                value={questionCount}
                onChange={(val) => setQuestionCount(val as number)}
                className="max-w-full"
                classNames={{
                  track: "bg-gray-200",
                  filler: "bg-cornflowerblue",
                }}
              />
            </div>

            {/* Question Types */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Question Types
              </label>
              <div className="space-y-2">
                <Checkbox
                  isSelected={questionTypes.multipleChoice}
                  onValueChange={(val) => setQuestionTypes({ ...questionTypes, multipleChoice: val })}
                  classNames={{ label: "text-sm text-gray-600" }}
                >
                  Multiple Choice
                </Checkbox>
                <Checkbox
                  isSelected={questionTypes.trueFalse}
                  onValueChange={(val) => setQuestionTypes({ ...questionTypes, trueFalse: val })}
                  classNames={{ label: "text-sm text-gray-600" }}
                >
                  True / False
                </Checkbox>
                <Checkbox
                  isSelected={questionTypes.shortAnswer}
                  onValueChange={(val) => setQuestionTypes({ ...questionTypes, shortAnswer: val })}
                  classNames={{ label: "text-sm text-gray-600" }}
                >
                  Short Answer
                </Checkbox>
                <Checkbox
                  isSelected={questionTypes.fillBlanks}
                  onValueChange={(val) => setQuestionTypes({ ...questionTypes, fillBlanks: val })}
                  classNames={{ label: "text-sm text-gray-600" }}
                >
                  Fill in the Blanks
                </Checkbox>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            color="primary"
            size="lg"
            className="w-full bg-cornflowerblue h-14 font-semibold"
            isLoading={isLoading}
            isDisabled={!canSubmit()}
            onClick={handleGenerate}
          >
            {isLoading ? "Generating Quiz..." : "Generate Quiz"}
          </Button>
        </div>
      </ToolPageLayout>

      <SignUpPromptModal
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        toolName="Quiz"
      />
    </>
  );
};

export default QuizGenerator;
